import React, { useState } from "react";
import { formatMoneyIntl } from "../../lib/formatMoney"; // relative: must REALLY resolve under Jest (no "@/" alias there)
import { Loader2, Tag, X } from "lucide-react";
import { api, extractApiError } from "@/lib/api";
import { useLanguage, useT } from "@/lib/LanguageContext";

/**
 * PromoField — the ONE promo-code input of every storefront (BUG-019).
 *
 * WHAT IT IS AND IS NOT. It is a PREVIEW widget. "Apply" calls
 * `GET /api/promo-codes/{code}`, which describes the code and says whether it is
 * redeemable *right now*, and increments nothing. It does NOT compute a new total and
 * never claims to: the code can expire, be deactivated or run out between this click and
 * the click on "Pay", and the price is decided server-side at checkout
 * (`promos.resolve_discount`, which answers 400 PROMO_INVALID). Every label therefore
 * says "applied at payment" — see i18n/dictionaries/public/promo.js.
 *
 * WHAT THE PARENT OWNS. The accepted code string, via `value` / `onChange`. The parent
 * ships it in its own checkout payload (`promo_code`), so there is exactly one request
 * per purchase and no half-applied state to reconcile. A parent that has not accepted a
 * code sends nothing.
 *
 * Props:
 *   kind        one of `modules/promos.DISCOUNTABLE_KINDS` ("booking" | "product" |
 *               "program" | "course" | "telehealth") — the purchase this field
 *               sits on. Used ONLY to refuse client-side a code the preview says does not
 *               cover this kind; the server decides again regardless.
 *   value       the accepted code (string, "" when none) — controlled by the parent.
 *   onChange    (code: string) => void, called with the accepted code or "" on removal.
 *   currency    ISO currency of the purchase, for rendering an amount discount.
 *   disabled    the parent is busy (a checkout in flight).
 */
export default function PromoField({ kind, value, onChange, currency, disabled = false }) {
  const { lang } = useLanguage();
  const t = useT("public");
  const [draft, setDraft] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // The purchase's own currency, handed in by the page; never a default of this field's.
  const fmtAmount = (cents) => formatMoneyIntl(cents, currency, lang);

  const apply = async () => {
    const code = draft.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(`/promo-codes/${encodeURIComponent(code)}`);
      // The preview's own verdict comes first; `applies_to` is checked second so a code
      // that is simply not for this page says so before the buyer reaches Stripe.
      if (!data?.redeemable) {
        setError(t("promo.notRedeemable"));
        return;
      }
      if (Array.isArray(data.applies_to) && !data.applies_to.includes(kind)) {
        setError(t("promo.invalid"));
        return;
      }
      setPreview(data);
      onChange(data.code);
      setDraft("");
    } catch (e) {
      // A 404 (unknown code) and the 400 PROMO_INVALID of a checkout are ONE message for
      // the buyer, matching the server, which deliberately does not distinguish them.
      setError(e?.response?.status === 404
        ? t("promo.invalid")
        : extractApiError(e, t("promo.invalid")));
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    setPreview(null);
    setError("");
    onChange("");
  };

  if (value && preview) {
    const label = preview.discount_type === "amount"
      ? t("promo.appliedAmount", { value: fmtAmount(preview.amount_cents) })
      : t("promo.appliedPct", { value: preview.percent_bp / 100 });
    return (
      <div
        data-testid="promo-applied"
        className="flex items-center gap-2 rounded-xl border border-eh-border-soft bg-eh-cream px-3 py-2.5 text-sm"
      >
        <Tag size={14} className="text-eh-sage-deep shrink-0" />
        <span className="font-mono text-eh-ink">{value}</span>
        <span className="text-eh-muted">· {label}</span>
        <button
          type="button"
          onClick={remove}
          disabled={disabled}
          aria-label={t("promo.remove")}
          className="ml-auto p-1 rounded-full text-eh-muted hover:text-eh-ink"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="promo-field">
      <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">
        {t("promo.label")}
      </label>
      <div className="mt-1 flex gap-2">
        <input
          type="text"
          value={draft}
          disabled={disabled || busy}
          placeholder={t("promo.placeholder")}
          onChange={(e) => { setDraft(e.target.value.toUpperCase()); setError(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
          className="flex-1 bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm font-mono"
          data-testid="promo-input"
        />
        <button
          type="button"
          onClick={apply}
          disabled={disabled || busy || !draft.trim()}
          className="btn-secondary text-sm px-4"
          data-testid="promo-apply"
        >
          {busy ? <Loader2 className="animate-spin" size={14} /> : t("promo.apply")}
        </button>
      </div>
      {busy && <div className="mt-1 text-xs text-eh-muted">{t("promo.checking")}</div>}
      {error && <div className="mt-1 text-xs text-red-600" data-testid="promo-error">{error}</div>}
    </div>
  );
}
